import { baseApi } from './baseApi';

// Table Event Types
export interface TableEvent {
  id: string;
  tableId: string;
  eventType: TableEventType;
  eventData: string; // JSON string containing event-specific data
  createdAt: string;
  createdBy: string;
  createdByName?: string;
  tableName?: string;
  areaName?: string;
}

export type TableEventType = 
  | 'TableOpened'
  | 'TableClosed'
  | 'TableClosedForce'
  | 'TableStatusChanged'
  | 'TableTransferred'
  | 'TableReassigned'
  | 'TableMerged'
  | 'TableSplit';

// Event Data Interfaces
export interface TableOpenedEventData {
  customerCount: number;
  adults?: number;
  children?: number;
  waiterId?: string;
  waiterName?: string;
  notes?: string;
  previousStatus: string;
}

export interface TableClosedEventData {
  orderId?: string;
  orderTotal?: number;
  paymentMethod?: string;
  reason?: string;
  previousStatus: string;
}

export interface TableClosedForceEventData {
  reason: string;
  authorizedBy: string;
  orderTotal?: number;
  orderId?: string;
  previousStatus: string;
}

export interface TableStatusChangedEventData {
  previousStatus: string;
  newStatus: string;
  reason?: string;
  authorizedBy?: string;
}

export interface TableTransferredEventData {
  fromTableId: string;
  fromTableNumber: string;
  fromAreaName: string;
  toTableId: string;
  toTableNumber: string;
  toAreaName: string;
  orderId?: string;
  authorizedBy: string;
  reason?: string;
}

export interface TableReassignedEventData {
  previousWaiterId?: string;
  previousWaiterName?: string;
  newWaiterId: string;
  newWaiterName: string;
  authorizedBy: string;
  reason?: string;
}

// API Request/Response Types
export interface CreateTableEventRequest {
  tableId: string;
  eventType: TableEventType;
  eventData: object;
  createdBy: string;
}

export interface GetTableEventsRequest {
  tableId: string;
  eventType?: TableEventType;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface GetTableEventsResponse {
  events: TableEvent[];
  totalCount: number;
  hasMore: boolean;
}

// Table Events API
export const tableEventsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create a new table event
    createTableEvent: builder.mutation<TableEvent, CreateTableEventRequest>({
      query: (data) => ({
        url: '/TableEvents',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['TableEvent'],
    }),

    // Get events for a specific table
    getTableEvents: builder.query<GetTableEventsResponse, GetTableEventsRequest>({
      query: ({ tableId, eventType, startDate, endDate, limit = 50, offset = 0 }) => {
        const params = new URLSearchParams({
          limit: limit.toString(),
          offset: offset.toString(),
        });
        
        if (eventType) params.append('eventType', eventType);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        
        return `/TableEvents/table/${tableId}?${params.toString()}`;
      },
      providesTags: (result, error, { tableId }) => [
        { type: 'TableEvent', id: tableId },
        { type: 'TableEvent', id: 'LIST' },
      ],
    }),

    // Get a specific table event by ID
    getTableEventById: builder.query<TableEvent, string>({
      query: (eventId) => `/TableEvents/${eventId}`,
      providesTags: (result, error, eventId) => [
        { type: 'TableEvent', id: eventId },
      ],
    }),

    // Get recent table events across all tables (for admin/monitoring)
    getRecentTableEvents: builder.query<GetTableEventsResponse, {
      eventType?: TableEventType;
      areaId?: string;
      limit?: number;
      offset?: number;
    }>({
      query: ({ eventType, areaId, limit = 20, offset = 0 }) => {
        const params = new URLSearchParams({
          limit: limit.toString(),
          offset: offset.toString(),
        });
        
        if (eventType) params.append('eventType', eventType);
        if (areaId) params.append('areaId', areaId);
        
        return `/TableEvents/recent?${params.toString()}`;
      },
      providesTags: [{ type: 'TableEvent', id: 'RECENT' }],
    }),

    // Get table events statistics
    getTableEventsStats: builder.query<{
      totalEvents: number;
      eventsByType: Record<TableEventType, number>;
      eventsToday: number;
      eventsThisWeek: number;
    }, {
      tableId?: string;
      areaId?: string;
      startDate?: string;
      endDate?: string;
    }>({
      query: ({ tableId, areaId, startDate, endDate }) => {
        const params = new URLSearchParams();
        
        if (tableId) params.append('tableId', tableId);
        if (areaId) params.append('areaId', areaId);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        
        return `/TableEvents/stats?${params.toString()}`;
      },
      providesTags: [{ type: 'TableEvent', id: 'STATS' }],
    }),
  }),
});

// Export hooks
export const {
  useCreateTableEventMutation,
  useGetTableEventsQuery,
  useGetTableEventByIdQuery,
  useGetRecentTableEventsQuery,
  useGetTableEventsStatsQuery,
} = tableEventsApi;

// Helper functions for creating event data
export const createTableEventData = {
  tableOpened: (data: Omit<TableOpenedEventData, 'previousStatus'>, previousStatus: string): TableOpenedEventData => ({
    ...data,
    previousStatus,
  }),

  tableClosed: (data: Omit<TableClosedEventData, 'previousStatus'>, previousStatus: string): TableClosedEventData => ({
    ...data,
    previousStatus,
  }),

  tableClosedForce: (data: Omit<TableClosedForceEventData, 'previousStatus'>, previousStatus: string): TableClosedForceEventData => ({
    ...data,
    previousStatus,
  }),

  tableStatusChanged: (previousStatus: string, newStatus: string, options?: { reason?: string; authorizedBy?: string }): TableStatusChangedEventData => ({
    previousStatus,
    newStatus,
    ...options,
  }),

  tableTransferred: (data: TableTransferredEventData): TableTransferredEventData => data,

  tableReassigned: (data: TableReassignedEventData): TableReassignedEventData => data,
};

export default tableEventsApi;